import {
  createColumnHelper,
  type PaginationState,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { ChevronDown, GraduationCap, LayoutGrid, List } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useGetPrivateLessonBookings } from '~entities/private-lessons/api/private-lessons';
import { useGetScheduledDrops } from '~entities/scheduled-drops/api/get-scheduled-drops';
import { useGetStudentById } from '~entities/student';
import { useGetClassHistoryByStudentId } from '~entities/student/api/get-class-history-by-student-id';
import {
  type Enrollment,
  type Event as StudentEvent,
} from '~entities/student/modal/student';
import { useCurrentStudio } from '~entities/studios/model/studio-store';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import {
  formatPrivateLessonSlotTime,
  getPrivateLessonDisplayTimezone,
} from '~features/private-lessons/ui/private-lesson-booking-helpers';
import { EnrollStudentModal } from '~features/students/ui/student-payment/enroll-students';

import { useGetCurrency } from '~shared/api/currency-api';
import {
  ClassDetailsCard,
  EventDetailsCard,
  TrialClassCard,
} from '~shared/components/class-details-panel';
import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import { formatClassTime, parseToNoonDate } from '~shared/helpers/datetime';
import { formatTrialDate } from '~shared/helpers/format-trial-datetime';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import { cn } from '~shared/shadcn/lib/utils';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '~shared/shadcn/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';
import {
  calculateAgeFromISO,
  formatDateIgnoreTimezone,
} from '~shared/utils/date-time';

interface StudentsClassPageProps {
  studentId: string;
}

function formatClassHistoryDateCell(value: unknown): string {
  if (value == null || value === '' || typeof value !== 'string') return '-';
  const formatted = formatDateIgnoreTimezone(value);
  return formatted === 'Invalid date' ? '-' : formatted;
}

function isPastDate(value: unknown): boolean {
  if (typeof value !== 'string' || value.trim() === '') return false;

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  parsedDate.setHours(0, 0, 0, 0);

  return parsedDate < today;
}

function isEnrollmentActiveForClassesTab(enrollment: Enrollment): boolean {
  const classEndDate = enrollment.enrollmentId?.endDate;
  const sessionEndDate = enrollment.enrollmentId?.session?.endDate;
  return !isPastDate(classEndDate) && !isPastDate(sessionEndDate);
}

function isEventActiveForClassesTab(event: StudentEvent): boolean {
  return !isPastDate(event.eventId?.endDate);
}

/** Unified Class History row: class rows keep their shape; event rows map
 * `eventId` onto `enrollmentId` so the shared columns render both. */
interface ClassHistoryRow {
  type: 'Class' | 'Event';
  enrollmentId: {
    _id: string;
    title: string;
    startDate: string;
    endDate: string;
    tuitionFee: number;
    session: {
      _id: string;
      name: string;
    } | null;
  } | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  droppingReason?: string | null;
  scheduledDropDate?: string | null;
}

const columnHelper = createColumnHelper<ClassHistoryRow>();

const columns = [
  columnHelper.accessor('enrollmentId.title', {
    id: 'Class Name',
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableHiding
          column={info.column}
          title='Class Name'
          className='pl-4 text-black'
        />
      );
    },
    cell: (info) => {
      const title = info.row.original.enrollmentId?.title;
      return <div className='px-4'>{title ?? '-'}</div>;
    },
  }),

  columnHelper.accessor('type', {
    id: 'Type',
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableHiding
          column={info.column}
          title='Type'
          className='pl-4 text-black'
        />
      );
    },
    cell: (info) => {
      return <div className='px-4'>{info.getValue()}</div>;
    },
  }),

  columnHelper.accessor('status', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableHiding
          column={info.column}
          title='Status'
          className='pl-4 text-black'
        />
      );
    },
    cell: (info) => {
      const status = info.getValue();
      return (
        <div className='px-4 capitalize'>
          {typeof status === 'string' ? status.split('-').join(' ') : '-'}
        </div>
      );
    },
  }),
  columnHelper.accessor('enrollmentId.session.name', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableHiding
          column={info.column}
          title='Session'
          className='pl-4 text-black'
        />
      );
    },
    cell: (info) => {
      const sessionName = info.row.original.enrollmentId?.session?.name;
      return <div className='px-4 capitalize'>{sessionName ?? '-'}</div>;
    },
  }),
  columnHelper.accessor('enrollmentId.tuitionFee', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableHiding
          column={info.column}
          title='Tuition Fee'
          className='pl-4 text-black'
        />
      );
    },
    cell: (info) => {
      const tuitionFee = info.row.original.enrollmentId?.tuitionFee;
      return <div className='px-4'>{tuitionFee ?? '-'}</div>;
    },
  }),
  columnHelper.accessor('enrollmentId.startDate', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableHiding
          column={info.column}
          title='Start Date'
          className='pl-4 text-black'
        />
      );
    },
    cell: (info) => (
      <div className='flex gap-2 px-4'>
        <span>{formatClassHistoryDateCell(info.getValue())}</span>
      </div>
    ),
  }),
  columnHelper.accessor('enrollmentId.endDate', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableHiding
          column={info.column}
          title='End Date'
          className='pl-4 text-black'
        />
      );
    },
    cell: (info) => (
      <div className='flex gap-2 px-4'>
        <span>{formatClassHistoryDateCell(info.getValue())}</span>
      </div>
    ),
  }),
  columnHelper.accessor('createdAt', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableHiding
          column={info.column}
          title='Enrolled Date'
          className='pl-4 text-black'
        />
      );
    },
    cell: (info) => (
      <div className='flex gap-2 px-4'>
        <span>{formatClassHistoryDateCell(info.getValue())}</span>
      </div>
    ),
  }),
  columnHelper.accessor('scheduledDropDate', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableHiding
          column={info.column}
          title='Scheduled Drop Date'
          className='pl-4 text-black'
        />
      );
    },
    cell: (info) => (
      <div className='flex gap-2 px-4'>
        <span>{formatClassHistoryDateCell(info.getValue())}</span>
      </div>
    ),
  }),
  columnHelper.accessor('droppingReason', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableHiding
          column={info.column}
          title='Dropping Reason'
          className='pl-4 text-xs text-black'
          disableSorting
        />
      );
    },
    cell: (info) => {
      const reason = info.getValue();
      if (!reason) return <div className='px-4'>-</div>;

      const truncatedReason =
        reason.length > 15 ? `${reason.substring(0, 15)}...` : reason;

      if (reason.length <= 15) {
        return <div className='px-4'>{reason}</div>;
      }

      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className='cursor-help px-4'>{truncatedReason}</div>
            </TooltipTrigger>
            <TooltipContent className='max-w-md break-words p-4 text-base'>
              <p>{reason}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    },
  }),
];

// New column helper for active enrollments table
const activeEnrollmentColumnHelper = createColumnHelper<Enrollment>();

const getActiveEnrollmentColumns = (currencySymbol: string) => [
  activeEnrollmentColumnHelper.accessor('enrollmentId.title', {
    id: 'Class Name',
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableHiding
          column={info.column}
          title='Class Name'
          className='pl-4 text-black'
        />
      );
    },
    cell: (info) => {
      const title = info.getValue();
      const archived = info.row.original.enrollmentId?.isArchive === true;
      return (
        <div className='flex flex-wrap items-center gap-2 px-4'>
          {archived ? (
            <Badge variant='secondary' className='shrink-0 text-xs font-normal'>
              Archived
            </Badge>
          ) : null}
          <span>{title}</span>
        </div>
      );
    },
  }),
  activeEnrollmentColumnHelper.accessor('enrollmentId.days', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableHiding
          column={info.column}
          title='Days'
          className='pl-4 text-black'
        />
      );
    },
    cell: (info) => {
      const days = info.getValue();
      if (!Array.isArray(days)) return <div className='px-4'>-</div>;
      return (
        <div className='flex flex-wrap gap-1 px-4'>
          {days.map((day: string) => (
            <span key={day} className='rounded bg-gray-100 px-1 text-xs'>
              {day}
            </span>
          ))}
        </div>
      );
    },
  }),
  activeEnrollmentColumnHelper.accessor(
    (row) => {
      const startTime = row.enrollmentId?.startTime ?? '';
      const endTime = row.enrollmentId?.endTime ?? '';
      return { startTime, endTime };
    },
    {
      id: 'time',
      header: (info) => {
        return (
          <DataTableColumnHeader
            disableHiding
            column={info.column}
            title='Time'
            className='pl-4 text-black'
          />
        );
      },
      cell: (info) => {
        const { startTime, endTime } = info.getValue();
        const formattedStart = formatClassTime(startTime);
        const formattedEnd = formatClassTime(endTime);
        if (!formattedStart || !formattedEnd) {
          return <div className='px-4'>-</div>;
        }
        return (
          <div className='px-4'>{`${formattedStart} - ${formattedEnd}`}</div>
        );
      },
    },
  ),
  activeEnrollmentColumnHelper.accessor(
    (row) => row.enrollmentId?.instructor[0]?.fieldName ?? '-',
    {
      id: 'instructor',
      header: (info) => {
        return (
          <DataTableColumnHeader
            disableHiding
            column={info.column}
            title='Instructor'
            className='pl-4 text-black'
          />
        );
      },
      cell: (info) => {
        return <div className='px-4'>{info.getValue()}</div>;
      },
    },
  ),
  activeEnrollmentColumnHelper.accessor('enrolledDate', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableHiding
          column={info.column}
          title='Enrollment Date'
          className='pl-4 text-black'
        />
      );
    },
    cell: (info) => {
      const value = info.getValue();
      const enrolledDate = value ? parseToNoonDate(value) : null;
      if (!enrolledDate) {
        return <div className='px-4'>-</div>;
      }
      return <div className='px-4'>{format(enrolledDate, 'MMMM d, yyyy')}</div>;
    },
  }),
  activeEnrollmentColumnHelper.accessor('enrollmentId.startDate', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableHiding
          column={info.column}
          title='Start Date'
          className='pl-4 text-black'
        />
      );
    },
    cell: (info) => {
      const value = info.getValue();
      const startDate = value ? parseToNoonDate(value) : null;
      if (!startDate) {
        return <div className='px-4'>-</div>;
      }
      return <div className='px-4'>{format(startDate, 'MMMM d, yyyy')}</div>;
    },
  }),
  activeEnrollmentColumnHelper.accessor('enrollmentId.endDate', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableHiding
          column={info.column}
          title='End Date'
          className='pl-4 text-black'
        />
      );
    },
    cell: (info) => {
      const value = info.getValue();
      const endDate = value ? parseToNoonDate(value) : null;
      if (!endDate) {
        return <div className='px-4'>-</div>;
      }
      return <div className='px-4'>{format(endDate, 'MMMM d, yyyy')}</div>;
    },
  }),
  activeEnrollmentColumnHelper.accessor('enrollmentId.tuitionFee', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableHiding
          column={info.column}
          title='Tuition Fee'
          className='pl-4 text-black'
        />
      );
    },
    cell: (info) => {
      const fee = info.getValue();
      return (
        <div className='px-4'>
          {fee ? `${currencySymbol}${Number(fee).toFixed(2)}` : '-'}
        </div>
      );
    },
  }),
];

// Column helper for the active events table — mirrors the active classes columns.
const activeEventColumnHelper = createColumnHelper<StudentEvent>();

const getActiveEventColumns = (currencySymbol: string) => [
  activeEventColumnHelper.accessor('eventId.title', {
    id: 'Event Name',
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableHiding
          column={info.column}
          title='Event Name'
          className='pl-4 text-black'
        />
      );
    },
    cell: (info) => {
      const title = info.getValue();
      const archived = info.row.original.eventId?.isArchive === true;
      return (
        <div className='flex flex-wrap items-center gap-2 px-4'>
          {archived ? (
            <Badge variant='secondary' className='shrink-0 text-xs font-normal'>
              Archived
            </Badge>
          ) : null}
          <span>{title}</span>
        </div>
      );
    },
  }),
  activeEventColumnHelper.accessor('eventId.days', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableHiding
          column={info.column}
          title='Days'
          className='pl-4 text-black'
        />
      );
    },
    cell: (info) => {
      const days = info.getValue();
      if (!Array.isArray(days)) return <div className='px-4'>-</div>;
      return (
        <div className='flex flex-wrap gap-1 px-4'>
          {days.map((day: string) => (
            <span key={day} className='rounded bg-gray-100 px-1 text-xs'>
              {day}
            </span>
          ))}
        </div>
      );
    },
  }),
  activeEventColumnHelper.accessor(
    (row) => {
      const startTime = row.eventId?.startTime ?? '';
      const endTime = row.eventId?.endTime ?? '';
      return { startTime, endTime };
    },
    {
      id: 'time',
      header: (info) => {
        return (
          <DataTableColumnHeader
            disableHiding
            column={info.column}
            title='Time'
            className='pl-4 text-black'
          />
        );
      },
      cell: (info) => {
        const { startTime, endTime } = info.getValue();
        const formattedStart = formatClassTime(startTime);
        const formattedEnd = formatClassTime(endTime);
        if (!formattedStart || !formattedEnd) {
          return <div className='px-4'>-</div>;
        }
        return (
          <div className='px-4'>{`${formattedStart} - ${formattedEnd}`}</div>
        );
      },
    },
  ),
  activeEventColumnHelper.accessor(
    (row) => row.eventId?.mainTeacher ?? '-',
    {
      id: 'instructor',
      header: (info) => {
        return (
          <DataTableColumnHeader
            disableHiding
            column={info.column}
            title='Instructor'
            className='pl-4 text-black'
          />
        );
      },
      cell: (info) => {
        return <div className='px-4'>{info.getValue()}</div>;
      },
    },
  ),
  activeEventColumnHelper.accessor(
    (row) => row.eventDate ?? row.enrolledDate,
    {
      id: 'enrolledDate',
      header: (info) => {
        return (
          <DataTableColumnHeader
            disableHiding
            column={info.column}
            title='Enrollment Date'
            className='pl-4 text-black'
          />
        );
      },
      cell: (info) => {
        const value = info.getValue();
        const enrolledDate = value ? parseToNoonDate(value) : null;
        if (!enrolledDate) {
          return <div className='px-4'>-</div>;
        }
        return (
          <div className='px-4'>{format(enrolledDate, 'MMMM d, yyyy')}</div>
        );
      },
    },
  ),
  activeEventColumnHelper.accessor('eventId.startDate', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableHiding
          column={info.column}
          title='Start Date'
          className='pl-4 text-black'
        />
      );
    },
    cell: (info) => {
      const value = info.getValue();
      const startDate = value ? parseToNoonDate(value) : null;
      if (!startDate) {
        return <div className='px-4'>-</div>;
      }
      return <div className='px-4'>{format(startDate, 'MMMM d, yyyy')}</div>;
    },
  }),
  activeEventColumnHelper.accessor('eventId.endDate', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableHiding
          column={info.column}
          title='End Date'
          className='pl-4 text-black'
        />
      );
    },
    cell: (info) => {
      const value = info.getValue();
      const endDate = value ? parseToNoonDate(value) : null;
      if (!endDate) {
        return <div className='px-4'>-</div>;
      }
      return <div className='px-4'>{format(endDate, 'MMMM d, yyyy')}</div>;
    },
  }),
  activeEventColumnHelper.accessor('eventId.tuitionFee', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableHiding
          column={info.column}
          title='Tuition Fee'
          className='pl-4 text-black'
        />
      );
    },
    cell: (info) => {
      const fee = info.getValue();
      return (
        <div className='px-4'>
          {fee ? `${currencySymbol}${fee.toFixed(2)}` : '-'}
        </div>
      );
    },
  }),
];

export const StudentsClassPage = ({ studentId }: StudentsClassPageProps) => {
  const { data: currencyDetails } = useGetCurrency();
  const currencySymbol = currencyDetails?.symbol ?? '$';
  const activeEnrollmentColumns = useMemo(
    () => getActiveEnrollmentColumns(currencySymbol),
    [currencySymbol],
  );
  const activeEventColumns = useMemo(
    () => getActiveEventColumns(currencySymbol),
    [currencySymbol],
  );

  const { data, isPending } = useGetStudentById(studentId, {
    includeArchivedEnrollments: true,
  });
  const { hasAdminAccess } = useCurrentUserRole();

  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [enrollStudentDialogOpen, setEnrollStudentDialogOpen] = useState(false);
  const [showEventHistory, setShowEventHistory] = useState(true);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getStoredPageSize(),
  });

  const { data: classHistory, isFetching: isFetchingHistory } =
    useGetClassHistoryByStudentId({
      studentId,
      limit: pagination.pageSize,
      page: pagination.pageIndex + 1,
      sortBy: 'createdAt',
      sortOrder: 'desc',
    });

  // Fetch all scheduled drops for this student
  const { data: scheduledDrops } = useGetScheduledDrops({
    studentId,
  });
  const { data: privateLessonBookingsData } = useGetPrivateLessonBookings({
    studentId,
  });
  const privateLessonBookings = privateLessonBookingsData?.bookings ?? [];
  const privateLessonListTimezone = privateLessonBookingsData?.timezone;
  const studioTimezone = useCurrentStudio()?.timezone;

  const studentAge = calculateAgeFromISO(data?.dob);

  // ENR-2522: do not blank Classes on background refetch (was isFetching).
  if (isPending && !data) {
    return null;
  }

  if (!data) {
    return <div>Student not found</div>;
  }

  // Merge scheduled drop data with class history (backend returns sorted by createdAt desc)
  // Slice to pageSize so we never show more than "rows per page" even if API over-returns
  const rawEnrollments = classHistory?.items[0]?.enrollments ?? [];
  const rawEvents = classHistory?.items[0]?.events ?? [];

  const classHistoryRows: Array<ClassHistoryRow> = rawEnrollments.map(
    (enrollment) => {
      // Find matching scheduled drop for this enrollment
      const scheduledDrop = scheduledDrops?.find(
        (drop) => drop.enrollmentId === enrollment.enrollmentId?._id,
      );

      return {
        ...enrollment,
        type: 'Class' as const,
        scheduledDropDate:
          scheduledDrop?.scheduledDropDate ?? enrollment.scheduledDropDate,
        droppingReason: scheduledDrop?.dropReason ?? enrollment.droppingReason,
      };
    },
  );

  const eventHistoryRows: Array<ClassHistoryRow> = showEventHistory
    ? rawEvents.map((event) => ({
        type: 'Event' as const,
        enrollmentId: event.eventId ?? null,
        status: event.status,
        createdAt: event.createdAt,
        updatedAt: event.updatedAt,
        droppingReason: event.droppingReason,
        scheduledDropDate: event.dropDate,
      }))
    : [];

  const tableData = [...classHistoryRows, ...eventHistoryRows]
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )
    .slice(0, pagination.pageSize);

  const activeEnrollments = data.enrollments
    ? data.enrollments.filter(
        (d) =>
          (d.subscriptionStatus === 'active' ||
            d.subscriptionStatus === 'scheduled' ||
            // Legacy charge pauses wrote financial state here. Keep visibility
            // stable while BE no longer writes this value for charge pauses.
            d.subscriptionStatus === 'paused') &&
          d.enrollmentId != null &&
          isEnrollmentActiveForClassesTab(d),
      )
    : [];

  const activeEvents = data.events
    ? data.events.filter(
        (event) =>
          (event.subscriptionStatus === 'active' ||
            event.subscriptionStatus === 'scheduled') &&
          event.eventId != null &&
          isEventActiveForClassesTab(event),
      )
    : [];

  const trialEnrollments =
    data.trialEnrollments?.filter((trial) =>
      ['Trial Scheduled', 'Trial Completed', 'Enrolled'].includes(
        trial.appointmentStatus ?? '',
      ),
    ) ?? [];

  const upcomingPrivateLessons = privateLessonBookings.filter(
    (booking) => booking.startTime && new Date(booking.startTime) >= new Date(),
  );

  return (
    <div className={cn('mt-8 pb-10')}>
      <div>
        {trialEnrollments.length > 0 && (
          <div className='mb-6 rounded-lg border-2 border-amber-200 bg-amber-50/80 px-4 py-3'>
            <h2 className='text-base font-semibold text-amber-800'>
              Trial Class
            </h2>
            <p className='text-sm text-amber-700'>
              {trialEnrollments.length} trial class
              {trialEnrollments.length === 1 ? '' : 'es'} booked
            </p>
          </div>
        )}
        {upcomingPrivateLessons.length > 0 && (
          <div className='mb-6 rounded-lg border-2 border-blue-200 bg-blue-50/80 px-4 py-3'>
            <h2 className='text-base font-semibold text-blue-800'>
              Private Lessons
            </h2>
            <div className='mt-3 grid gap-2 md:grid-cols-2'>
              {upcomingPrivateLessons.map((booking) => {
                const offering =
                  typeof booking.offeringId === 'string'
                    ? null
                    : booking.offeringId;
                const displayTimezone = getPrivateLessonDisplayTimezone(
                  booking,
                  privateLessonListTimezone,
                  studioTimezone,
                );

                return (
                  <div
                    key={booking._id}
                    className='rounded-md border bg-white p-3 text-sm'
                  >
                    <div className='flex items-center justify-between gap-2'>
                      <p className='font-medium'>
                        {offering?.title ?? 'Private lesson'}
                      </p>
                      <Badge
                        variant={
                          booking.paymentStatus === 'paid'
                            ? 'default'
                            : 'secondary'
                        }
                      >
                        {booking.paymentStatus}
                      </Badge>
                    </div>
                    <p className='text-muted-foreground'>
                      {formatTrialDate(
                        booking.startTime,
                        displayTimezone,
                        'MMM d, yyyy',
                      ) ?? '-'}{' '}
                      · {formatPrivateLessonSlotTime(booking, displayTimezone)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div className='mb-3 mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6'>
          <div className='flex w-full items-center justify-between gap-3 sm:w-auto sm:justify-start sm:gap-4'>
            <div className='flex rounded-md border'>
              <Button
                variant={viewMode === 'cards' ? 'default' : 'ghost'}
                size='sm'
                className='rounded-r-none'
                aria-label='Cards view'
                onClick={() => {
                  setViewMode('cards');
                }}
              >
                <LayoutGrid className='size-4' />
              </Button>
              <Button
                variant={viewMode === 'table' ? 'default' : 'ghost'}
                size='sm'
                className='rounded-l-none'
                aria-label='List view'
                onClick={() => {
                  setViewMode('table');
                }}
              >
                <List className='size-4' />
              </Button>
            </div>
            <p className={cn('min-w-28 text-right text-base text-black sm:text-left')}>
              Classes: {activeEnrollments.length} | Events:{' '}
              {activeEvents.length} | Trials: {trialEnrollments.length}
            </p>
          </div>
          {hasAdminAccess && (
            <Button
              size='sm'
              onClick={() => {
                setEnrollStudentDialogOpen(true);
              }}
            >
              <GraduationCap className='size-4' />
              Enroll Student
            </Button>
          )}
        </div>

        <EnrollStudentModal
          studentId={studentId}
          dialogOpen={enrollStudentDialogOpen}
          setDialogOpen={setEnrollStudentDialogOpen}
        />

        {viewMode === 'cards' ? (
          <div className='flex flex-wrap gap-6'>
            {trialEnrollments.map((trial) => (
              <TrialClassCard key={trial._id} trial={trial} />
            ))}
            {activeEnrollments.map((enrollment) => (
              <ClassDetailsCard
                key={enrollment.enrollmentId?._id ?? ''}
                enrollment={enrollment}
                studentName={data.name}
                studentAge={studentAge}
                studioId={data.studioId}
                studentId={studentId}
                parentId={
                  typeof data.parentId === 'string'
                    ? data.parentId
                    : (data.parentId as { _id?: string } | undefined)?._id ??
                      ''
                }
              />
            ))}
          </div>
        ) : (
          <div className='mt-2'>
            <EnrollioTable
              ariaLabel='Active student classes'
              totalLabel='Active Classes'
              filterHeader={(table) => (
                <EnrollioTableSearch
                  placeholder='Search active classes...'
                  value={
                    (table.getColumn('Class Name')?.getFilterValue() as
                      | string
                      | undefined) ?? ''
                  }
                  onChange={(value) => {
                    table.getColumn('Class Name')?.setFilterValue(value);
                  }}
                />
              )}
              toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
              data={activeEnrollments}
              columns={activeEnrollmentColumns}
              isUpdating={false}
            />
          </div>
        )}

        {viewMode === 'table' && trialEnrollments.length > 0 && (
          <div className='mt-6 rounded-md border p-4'>
            <h3 className='mb-3 text-sm font-semibold'>Trial Classes</h3>
            <div className='space-y-2'>
              {trialEnrollments.map((trial) => (
                <div
                  key={trial._id}
                  className='flex items-center justify-between rounded-md border bg-amber-50 p-3'
                >
                  <div className='text-sm'>
                    {trial.classId?.title ?? trial.className ?? 'Trial Class'}
                  </div>
                  <Badge className='bg-amber-500 text-white'>Trial Class</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className='mt-8'>
          <p className={cn('min-w-28 text-base text-black')}>
            Events: {activeEvents.length}
          </p>
          {viewMode === 'cards' ? (
            <div className='mt-3 flex w-full flex-col gap-6 md:flex-row md:flex-wrap'>
              {activeEvents.map((event) => (
                <EventDetailsCard
                  key={event.eventId?._id ?? ''}
                  event={event}
                  studentId={studentId}
                  studioId={data.studioId}
                />
              ))}
            </div>
          ) : (
            <div className='mt-3'>
              <EnrollioTable
                ariaLabel='Active student events'
                totalLabel='Active Events'
                filterHeader={(table) => (
                  <EnrollioTableSearch
                    placeholder='Search active events...'
                    value={
                      (table.getColumn('Event Name')?.getFilterValue() as
                        | string
                        | undefined) ?? ''
                    }
                    onChange={(value) => {
                      table.getColumn('Event Name')?.setFilterValue(value);
                    }}
                  />
                )}
                toolbar={(table) => (
                  <EnrollioTableColumnToggle table={table} />
                )}
                data={activeEvents}
                columns={activeEventColumns}
                isUpdating={false}
              />
            </div>
          )}
        </div>

        <Collapsible open={isOpen} onOpenChange={setIsOpen} className=''>
          <div className='mt-8 flex w-[350px] items-center justify-between'>
            <h2 className='text-xl font-bold'>Class History</h2>
            <CollapsibleTrigger asChild>
              <Button variant='ghost' size='sm'>
                <ChevronDown
                  className={cn(
                    'size-4 transition-all',
                    isOpen && 'rotate-180',
                  )}
                />
                <span className='sr-only'>Toggle</span>
              </Button>
            </CollapsibleTrigger>
          </div>

          <CollapsibleContent className='mt-4'>
            <EnrollioTable
              ariaLabel='Student class history'
              totalLabel='Class History'
              filterHeader={(table) => (
                <div className='flex flex-wrap items-center gap-4'>
                  <EnrollioTableSearch
                    placeholder='Search class history...'
                    value={
                      (table.getColumn('Class Name')?.getFilterValue() as
                        | string
                        | undefined) ?? ''
                    }
                    onChange={(value) => {
                      table.getColumn('Class Name')?.setFilterValue(value);
                    }}
                  />
                  <label className='flex items-center gap-2 text-sm text-black'>
                    <Checkbox
                      checked={showEventHistory}
                      onCheckedChange={(checked) => {
                        setShowEventHistory(checked === true);
                      }}
                    />
                    Show events
                  </label>
                </div>
              )}
              toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
              data={tableData}
              columns={columns}
              rowCount={classHistory?.pagination.total ?? 0}
              isUpdating={isFetchingHistory}
              pagination={pagination}
              onPaginationChange={setPagination}
              manualPagination
            />
          </CollapsibleContent>
        </Collapsible>
      </div>
    </div>
  );
};
